'use client';

import React, { useState, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import ReactModal from 'react-modal';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import momenttz from 'moment-timezone';
import moment from 'moment-timezone';
import 'moment/min/locales';

import UserContext from '../src/contexts/user.context';
import { SportContext, ThemeContext } from '@dstantwo/ullr-react-components';
import CONSTANTS, { ColorTheme } from '../src/constants';
import User from '../src/models/User';
import UllrApi from '../src/services/ullrApi';
import DataProvider from '../src/data/DataProvider';
import { Sport } from '../src/types/enums';

export function Providers({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | false>(false);
  const [sport, setSport] = useState<Sport>(CONSTANTS.SPORTS.SNOW);
  const [themeColors, setThemeColors] = useState<ColorTheme>(CONSTANTS.COLORS_SNOW);

  useEffect(() => {
    // react-modal needs the app root for accessibility (aria-hides it when a
    // modal opens). Set once on the client; otherwise it warns on every modal.
    ReactModal.setAppElement('body');
  }, []);

  useEffect(() => {
    // restore saved sport on page load
    const storedSport = localStorage.getItem('ullrSport');
    if (storedSport && CONSTANTS.SPORTS[storedSport as keyof typeof CONSTANTS.SPORTS]) {
      setSport(storedSport as Sport);
    }
  }, []);

  useEffect(() => {
    // set moment locale to user locale from browser
    const browserLocale = window.navigator.language;
    momenttz.locale(browserLocale);
    moment.locale(browserLocale);
  }, []);

  useEffect(() => {
    const sportColors = CONSTANTS.COLORS(sport);
    setThemeColors(sportColors);
    // Drive sport-aware shadcn tokens ([data-sport="BIKE"] in globals.css)
    document.documentElement.setAttribute('data-sport', sport);
  }, [sport]);

  useEffect(() => {
    if (user) {
      datadogRum.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
      });
    } else {
      datadogRum.clearUser();
    }
  }, [user]);

  // Attempt to fetch user on page load (fire-once session bootstrap).
  useEffect(() => {
    try {
      UllrApi.user.getUserNoInterceptors().then((newUser) => {
        setUser((prev) => {
          if (newUser) {
            return new User(newUser);
          }
          // A login may have completed (setCurrentUser) while this bootstrap
          // check was in flight. Don't clobber that fresh user with a stale
          // "no session" result — only fall through to null if we never had a
          // user. (ULLR-130)
          return prev ? prev : null;
        });
      });
    } catch (error) {
      // do nothing; swallow error
    }
  }, []);

  const sportContextValue = {
    sport,
    setSport: (newSport: Sport) => {
      localStorage.setItem('ullrSport', newSport || '');
      if (newSport !== sport) {
        setSport(newSport);
      }
    },
  };

  const userContextValue = {
    user,
    setCurrentUser: (newUser: any) => setUser(new User(newUser || {})),
  };

  return (
    <SportContext.Provider value={sportContextValue}>
      <ThemeContext.Provider value={{ COLORS: themeColors }}>
        <UserContext.Provider value={userContextValue}>
          {/* SWR data layer (src/data): app-wide cache + revalidation defaults */}
          <DataProvider>
            {children}
          </DataProvider>
          <ToastContainer position="bottom-left" newestOnTop limit={5} />
        </UserContext.Provider>
      </ThemeContext.Provider>
    </SportContext.Provider>
  );
}
